import type { LevelDefinition, ProgressRepository } from "@/domain";
import type {
  PlayRecommendation,
  PlaySessionCompletion,
  PlaySessionSaveResult,
  PlaySessionSaver
} from "@/features/play";
import {
  AttemptSaveController,
  type AttemptGeneration,
  type AttemptSaveCommandResult
} from "@/features/session";

interface AttemptSaveDriver {
  beginAttempt(context: {
    readonly profileId: string;
    readonly level: LevelDefinition;
    readonly recommendationLevels: readonly LevelDefinition[];
  }): AttemptGeneration;
  completeAttempt(
    generation: AttemptGeneration,
    metrics: PlaySessionCompletion["metrics"]
  ): Promise<AttemptSaveCommandResult>;
  retry(generation: AttemptGeneration): Promise<AttemptSaveCommandResult>;
}

export interface AttemptControllerPlaySessionSaverOptions {
  readonly controller: AttemptSaveDriver;
  readonly recommendationLevels: readonly LevelDefinition[];
}

export interface CreateAppPlaySessionSaverOptions {
  readonly isPersistent: boolean;
  readonly recommendationLevels: readonly LevelDefinition[];
  readonly repository: ProgressRepository;
}

export class PlayProgressSaveError extends Error {
  override readonly name = "PlayProgressSaveError";

  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
  }
}

function toRecommendation(
  result: Exclude<AttemptSaveCommandResult, { readonly status: "stale" }>
): PlayRecommendation | null {
  if (result.status === "error") {
    return null;
  }

  const recommendation = result.progress.nextRecommendation;
  return recommendation === null
    ? null
    : {
        levelId: recommendation.level.id,
        title: recommendation.level.title
      };
}

function toPlaySaveResult(
  result: AttemptSaveCommandResult
): PlaySessionSaveResult {
  if (result.status === "stale") {
    throw new PlayProgressSaveError(
      "The completed play attempt is no longer the active save generation."
    );
  }

  if (result.status === "error") {
    throw new PlayProgressSaveError(
      "The completed play attempt could not be saved at " +
        result.failedStage +
        ".",
      { cause: result.error }
    );
  }

  return {
    navigation: result.navigation,
    nextRecommendation: toRecommendation(result)
  };
}

export class AttemptControllerPlaySessionSaver implements PlaySessionSaver {
  private readonly controller: AttemptSaveDriver;
  private readonly recommendationLevels: readonly LevelDefinition[];
  private readonly generations = new WeakMap<
    PlaySessionCompletion,
    AttemptGeneration
  >();

  constructor({
    controller,
    recommendationLevels
  }: AttemptControllerPlaySessionSaverOptions) {
    this.controller = controller;
    this.recommendationLevels = recommendationLevels;
  }

  async saveCompletion(
    completion: PlaySessionCompletion
  ): Promise<PlaySessionSaveResult> {
    const existingGeneration = this.generations.get(completion);

    if (existingGeneration !== undefined) {
      return toPlaySaveResult(
        await this.controller.retry(existingGeneration)
      );
    }

    const generation = this.controller.beginAttempt({
      profileId: completion.profileId,
      level: completion.level,
      recommendationLevels: this.recommendationLevels.filter(
        ({ domain }) => domain === completion.level.domain
      )
    });
    this.generations.set(completion, generation);

    return toPlaySaveResult(
      await this.controller.completeAttempt(
        generation,
        completion.metrics
      )
    );
  }
}

export function createAppPlaySessionSaver({
  isPersistent,
  recommendationLevels,
  repository
}: CreateAppPlaySessionSaverOptions): PlaySessionSaver {
  return new AttemptControllerPlaySessionSaver({
    controller: new AttemptSaveController({ repository, isPersistent }),
    recommendationLevels
  });
}
